import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, CheckCircle, XCircle, Loader2, User } from 'lucide-react';
import { postData } from '../../services/NodeServices';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const SignupPageTailwind = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordChecks, setPasswordChecks] = useState({
    hasNumber: false,
    hasLetter: false,
    minLength: false
  });
 

  const navigate = useNavigate()
  // Name validation
  useEffect(() => {
    if (firstName && firstName.length < 2) {
      setNameError('First name must be at least 2 characters');
    } else if (lastName && lastName.length < 2) {
      setNameError('Last name must be at least 2 characters');
    } else if (firstName && !/^[a-zA-Z\s]+$/.test(firstName)) {
      setNameError('First name can only contain letters');
    } else if (lastName && !/^[a-zA-Z\s]+$/.test(lastName)) {
      setNameError('Last name can only contain letters');
    } else {
      setNameError('');
    }
  }, [firstName, lastName]);

  // Email validation
  useEffect(() => {
    if (email && !email.includes('@')) {
      setEmailError('Email Address Must Contain @');
    } else if (email && !email.includes('.')) {
      setEmailError('Email must contain a valid domain');
    } else if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  }, [email]);

  // Password validation
  useEffect(() => {
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Z]/.test(password);
    const minLength = password.length >= 8;
    
    setPasswordChecks({
      hasNumber,
      hasLetter,
      minLength
    });

    if (password && !minLength) {
      setPasswordError('Password must be at least 8 characters');
    } else if (password && !hasNumber) {
      setPasswordError('Password must contain at least 1 number');
    } else if (password && !hasLetter) {
      setPasswordError('Password must contain at least 1 letter');
    } else {
      setPasswordError('');
    }
  }, [password]);

  // Confirm password validation
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  }, [password, confirmPassword]);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (emailError || passwordError || confirmPasswordError || nameError || 
        !firstName || !lastName || !email || !password || !confirmPassword) return;
    
    setIsLoading(true);
    // Simulate API call
    

const body = {
            email: email,
            password: password,
            firstname: firstName,
            lastname: lastName,
            mobile: ''
        };

        if (password && email) {
            try {
                const result = await postData('api/users/signu', body);

                if (result.status) {
                    toast.success('Successfully signed up!');
                  /*   loginUser(result.data.user);
                    const redirectPath = new URLSearchParams(location.search).get('redirect') || '/'; */
                    navigate('/log-in');
                } else {
                    toast.error(result.message);
                }
            } catch {
                toast.error('Server error');
            }
        }






  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <div className="flex bg-[#fff] justify-center items-center min-h-screen w-full">
      {/* Signup Form */}
      <div className="flex items-center justify-center p-8 w-full max-w-[575px] max-sm:px-1">
        <motion.div
          className="w-full max-w-md p-8 max-sm:px-1"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 
            className="text-2xl font-semibold text-gray-800 mb-8"
            variants={itemVariants}
          >
           
          </motion.h1>

          <motion.div 
            className="text-center w-full justify-start flex text-gray-500 mb-6"
            variants={itemVariants}
          >
            Sign Up
          </motion.div>

          {/* Signup Form */}
          <div className="space-y-6">
            {/* Name Fields */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className={`w-full px-8 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                      nameError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  {firstName && (
                    <motion.div
                      className="absolute right-3 top-3.5"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      {nameError ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                  Last Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    className={`w-full px-8 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                      nameError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  {lastName && (
                    <motion.div
                      className="absolute right-3 top-3.5"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      {nameError ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>

            <AnimatePresence>
              {nameError && (
                <motion.p
                  className="text-red-500 text-sm w-full text-left"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {nameError}
                </motion.p>
              )}
            </AnimatePresence>

            {/* Email Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                Email address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="johndoe666@gmail.com"
                  className={`w-full px-8 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                    emailError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                {email && (
                  <motion.div
                    className="absolute right-3 top-3.5"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    {emailError ? (
                      <XCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </motion.div>
                )}
              </div>
              <AnimatePresence>
                {emailError && (
                  <motion.p
                    className="text-red-500 text-sm mt-2 w-full text-left"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {emailError}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Password Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full px-8 py-3 pl-11 pr-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                    passwordError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <motion.button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </motion.button>
              </div>

              {/* Password Requirements */}
              <AnimatePresence>
                {password && (
                  <motion.div
                    className="mt-3 space-y-2"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        {passwordChecks.hasNumber ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </motion.div>
                      <span className={passwordChecks.hasNumber ? 'text-green-600' : 'text-red-600'}>
                        at least 1 number
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        {passwordChecks.hasLetter ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </motion.div>
                      <span className={passwordChecks.hasLetter ? 'text-green-600' : 'text-red-600'}>
                        at least 1 letter
                      </span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        {passwordChecks.minLength ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </motion.div>
                      <span className={passwordChecks.minLength ? 'text-green-600' : 'text-red-600'}>
                        at least 8 characters
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {passwordError && (
                  <motion.p
                    className="text-red-500 text-sm mt-1 w-full text-left"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {passwordError}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Confirm Password Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full px-8 py-3 pl-11 pr-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                    confirmPasswordError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <motion.button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </motion.button>
              </div>
              <AnimatePresence>
                {confirmPasswordError && (
                  <motion.p
                    className="text-red-500 text-sm mt-2 w-full text-left"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {confirmPasswordError}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Signup Button */}
            <motion.button
              onClick={handleSignup}
              disabled={isLoading || emailError || passwordError || confirmPasswordError || nameError || 
                       !firstName || !lastName || !email || !password || !confirmPassword}
              className="w-full bg-[#222] text-white py-3 px-4 rounded-full font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
              variants={itemVariants}
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </div>
              ) : (
                'Sign Up'
              )}
            </motion.button>

            {/* Login Link */}
            <motion.div 
              className="text-left w-full"
              variants={itemVariants}
            >
              <span className="text-gray-600 text-sm w-full text-left">Already have an account?</span>
              <motion.a
                href="#"
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm ml-2"
                whileHover={{ scale: 1.05 }}
              >
                Login
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupPageTailwind;